import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react-native";
import { ArrowLeft01Icon, ArrowRight01Icon } from "@hugeicons-pro/core-solid-rounded";

import { useAppContext } from "../API/Context-API/AppContext";
import { scaleAll_ } from "../API/Scale";
import { AutoHeighImage } from "../Image/AutoWidthImage";
import type { CarouselImagesProps } from "./CarouselImages";
import "../../../styles/main.css";

type TransitionState = {
  from: number;
  to: number;
  fromPct: number;
  toPct: number;
} | null;

type SwipeState = {
  pointerId: number;
  startX: number;
  startY: number;
  deltaX: number;
  isSwiping: boolean;
} | null;

const CarouselImagesWeb:React.FC<CarouselImagesProps> = ({
  data = [],
  addButtons = false,
  width = 300,
  height = 400,
  addPagination = true,
  renderItem = null,
  carouselProps,
}) => {
  const { scaleAll, isSmallVersion } = useAppContext();
  const [index, setIndex] = useState(carouselProps?.defaultIndex ?? 0);
  const [transition, setTransition] = useState<TransitionState>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState(width);
  const swipeRef = useRef<SwipeState>(null);
  const suppressClickRef = useRef(false);

  const getThreshold = useCallback(() => {
    return Math.min(Math.max(measuredWidth * 0.12, 32), 72);
  }, [measuredWidth]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;

    const updateWidth = () => {
      const nextWidth = Math.round(node.getBoundingClientRect().width);
      if (nextWidth > 0) setMeasuredWidth(nextWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setIndex((prev) => {
      if (data.length === 0) return 0;
      return Math.min(prev, data.length - 1);
    });
    setTransition(null);
  }, [data.length]);

  const safeIndex = Math.min(index, Math.max(data.length - 1, 0));
  const hasMultipleSlides = data.length > 1;

  const defaultRenderItem = useCallback(
    ({ item }: { item: any; index: number }) => {
      return (
        <AutoHeighImage
          width={measuredWidth}
          scaleAll={scaleAll}
          source={{
            uri: item,
          }}
          maxHeight={400}
        />
      );
    },
    [measuredWidth, scaleAll]
  );

  const renderSlide = useCallback(
    (item: any, itemIndex: number) => {
      return renderItem
        ? renderItem({ item, index: itemIndex })
        : defaultRenderItem({ item, index: itemIndex });
    },
    [defaultRenderItem, renderItem]
  );

  const startTransition = useCallback(
    (
      targetIndex: number,
      directionOverride?: -1 | 1,
      startOffsetPx = 0
    ) => {
      if (!hasMultipleSlides) return;
      if (targetIndex === safeIndex) return;

      const direction: -1 | 1 =
        directionOverride ?? (targetIndex > safeIndex ? 1 : -1);
      const startOffsetPct =
        measuredWidth > 0 ? (startOffsetPx / measuredWidth) * 100 : 0;

      setTransition({
        from: safeIndex,
        to: targetIndex,
        fromPct: startOffsetPct,
        toPct: startOffsetPct + direction * 100,
      });

      requestAnimationFrame(() => {
        setTransition((prev) =>
          prev
            ? {
                ...prev,
                fromPct: -direction * 100,
                toPct: 0,
              }
            : prev
        );
      });
    },
    [hasMultipleSlides, measuredWidth, safeIndex]
  );

  const handlePrev = useCallback(() => {
    const target = safeIndex <= 0 ? data.length - 1 : safeIndex - 1;
    startTransition(target, -1);
  }, [data.length, safeIndex, startTransition]);

  const handleNext = useCallback(() => {
    const target = safeIndex >= data.length - 1 ? 0 : safeIndex + 1;
    startTransition(target, 1);
  }, [data.length, safeIndex, startTransition]);

  const stopEvent = useCallback((event: {
    preventDefault: () => void;
    stopPropagation: () => void;
  }) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handlePrevClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      stopEvent(event);
      handlePrev();
    },
    [handlePrev, stopEvent]
  );

  const handleNextClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      stopEvent(event);
      handleNext();
    },
    [handleNext, stopEvent]
  );

  const handleDotClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, targetIndex: number) => {
      stopEvent(event);
      startTransition(targetIndex);
    },
    [startTransition, stopEvent]
  );

  const handleTransitionEnd = useCallback(() => {
    if (!transition) return;
    setIndex(transition.to);
    setTransition(null);
    setDragOffset(0);
  }, [transition]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!hasMultipleSlides || transition) return;
      event.currentTarget.setPointerCapture?.(event.pointerId);

      swipeRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        deltaX: 0,
        isSwiping: false,
      };
      suppressClickRef.current = false;
    },
    [hasMultipleSlides, transition]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const swipe = swipeRef.current;
      if (!swipe || swipe.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - swipe.startX;
      const deltaY = event.clientY - swipe.startY;

      swipe.deltaX = deltaX;

      if (!swipe.isSwiping && Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY)) {
        swipe.isSwiping = true;
        suppressClickRef.current = true;
      }

      if (swipe.isSwiping) {
        setDragOffset(deltaX);
        event.preventDefault();
        event.stopPropagation();
      }
    },
    []
  );

  const finishSwipe = useCallback(() => {
    const swipe = swipeRef.current;
    swipeRef.current = null;
    if (!swipe || !swipe.isSwiping) {
      setDragOffset(0);
      return;
    }

    const threshold = getThreshold();

    if (swipe.deltaX <= -threshold) {
      const target = safeIndex >= data.length - 1 ? 0 : safeIndex + 1;
      startTransition(target, 1, swipe.deltaX);
    } else if (swipe.deltaX >= threshold) {
      const target = safeIndex <= 0 ? data.length - 1 : safeIndex - 1;
      startTransition(target, -1, swipe.deltaX);
    } else {
      setDragOffset(0);
    }
  }, [data.length, getThreshold, safeIndex, startTransition]);

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const swipe = swipeRef.current;
      if (!swipe || swipe.pointerId !== event.pointerId) return;

      if (swipe.isSwiping) {
        event.preventDefault();
        event.stopPropagation();
      }

      finishSwipe();
    },
    [finishSwipe]
  );

  const handlePointerCancel = useCallback(() => {
    swipeRef.current = null;
    setDragOffset(0);
  }, []);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!hasMultipleSlides || transition || swipeRef.current) return;

      const rect = event.currentTarget.getBoundingClientRect();
      if (rect.width <= 0 || data.length <= 1) return;

      const relativeX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
      const percent = relativeX / rect.width;
      const targetIndex = Math.min(
        data.length - 1,
        Math.floor(percent * data.length)
      );

      if (targetIndex !== safeIndex) {
        setIndex(targetIndex);
      }
    },
    [data.length, hasMultipleSlides, safeIndex, transition]
  );

  const handleMouseLeave = useCallback(() => {
    if (swipeRef.current?.isSwiping) return;
    setDragOffset(0);
  }, []);

  const handleViewportClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!suppressClickRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
    },
    []
  );

  const dotSize = isSmallVersion ? 5 : 10;

  const paginationContainerStyle = useMemo(
    () => ({
      gap: `${5 * scaleAll}px`,
      marginTop: `${10 * scaleAll}px`,
    }),
    [scaleAll]
  );

  const viewportStyle = useMemo(
    () =>
      ({
        width: "100%",
        height: `${height}px`,
      }) as React.CSSProperties,
    [height]
  );

  const singleSlideStyle = useMemo(
    () =>
      ({
        width: `${measuredWidth}px`,
        height: `${height}px`,
      }) as React.CSSProperties,
    [height, measuredWidth]
  );

  const getTransitionStyle = useCallback(
    (kind: "from" | "to") => {
      if (!transition) return singleSlideStyle;
      const translatePct = kind === "from" ? transition.fromPct : transition.toPct;

      return {
        ...singleSlideStyle,
        transform: `translate3d(${translatePct}%, 0, 0)`,
      } as React.CSSProperties;
    },
    [singleSlideStyle, transition]
  );

  const dragDirection: -1 | 1 | 0 = dragOffset === 0 ? 0 : dragOffset < 0 ? 1 : -1;
  const dragTargetIndex =
    dragDirection === 1
      ? safeIndex >= data.length - 1
        ? 0
        : safeIndex + 1
      : dragDirection === -1
      ? safeIndex <= 0
        ? data.length - 1
        : safeIndex - 1
      : safeIndex;

  const dragPct = measuredWidth > 0 ? (dragOffset / measuredWidth) * 100 : 0;
  const isDragging = !transition && dragDirection !== 0 && Math.abs(dragOffset) > 0;

  const currentDragStyle = useMemo(
    () =>
      ({
        ...singleSlideStyle,
        transform: `translate3d(${dragPct}%, 0, 0)`,
      }) as React.CSSProperties,
    [dragPct, singleSlideStyle]
  );

  const adjacentDragStyle = useMemo(
    () =>
      ({
        ...singleSlideStyle,
        transform: `translate3d(${dragPct + dragDirection * 100}%, 0, 0)`,
      }) as React.CSSProperties,
    [dragDirection, dragPct, singleSlideStyle]
  );

  if (data.length === 0) {
    return <div className="main-carousel" />;
  }

  return (
    <div ref={containerRef} className="main-carousel">
      <div className="main-carousel__viewport" style={viewportStyle}>
      <div
        className="main-carousel__swipe-layer"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClickCapture={handleViewportClickCapture}
      >
        {transition ? (
          <>
            <div
              className="main-carousel__slide main-carousel__slide--animated"
              style={getTransitionStyle("from")}
              onTransitionEnd={handleTransitionEnd}
            >
              {renderSlide(data[transition.from], transition.from)}
            </div>
            <div
              className="main-carousel__slide main-carousel__slide--animated"
              style={getTransitionStyle("to")}
            >
              {renderSlide(data[transition.to], transition.to)}
            </div>
          </>
        ) : isDragging ? (
          <>
            <div className="main-carousel__slide" style={currentDragStyle}>
              {renderSlide(data[safeIndex], safeIndex)}
            </div>
            <div className="main-carousel__slide" style={adjacentDragStyle}>
              {renderSlide(data[dragTargetIndex], dragTargetIndex)}
            </div>
          </>
        ) : (
          <div className="main-carousel__slide" style={singleSlideStyle}>
            {renderSlide(data[safeIndex], safeIndex)}
          </div>
        )}
        {addButtons && hasMultipleSlides && (
          <>
            <button
              type="button"
              onClick={handlePrevClick}
              onMouseDown={stopEvent}
              onPointerDown={stopEvent}
              onTouchStart={stopEvent}
              className="main-carousel__button main-carousel__button--left"
            >
              <HugeiconsIcon
                icon={ArrowLeft01Icon}
                color="black"
                size={scaleAll_(30)}
              />
            </button>
            <button
              type="button"
              onClick={handleNextClick}
              onMouseDown={stopEvent}
              onPointerDown={stopEvent}
              onTouchStart={stopEvent}
              className="main-carousel__button main-carousel__button--right"
            >
              <HugeiconsIcon
                icon={ArrowRight01Icon}
                color="black"
                size={scaleAll_(30)}
              />
            </button>
          </>
        )}
      </div>
      </div>

      {addPagination && hasMultipleSlides && (
        <div className="main-carousel__pagination" style={paginationContainerStyle}>
          {data.map((_, dotIndex) => (
            <button
              key={dotIndex}
              type="button"
              onClick={(event) => handleDotClick(event, dotIndex)}
              onMouseDown={stopEvent}
              onPointerDown={stopEvent}
              onTouchStart={stopEvent}
              className="main-carousel__dot-button"
            >
              <span
                className={
                  dotIndex === safeIndex
                    ? "main-carousel__dot"
                    : "main-carousel__dot main-carousel__dot--inactive"
                }
                style={{
                  width: `${dotSize}px`,
                  height: `${dotSize}px`,
                  borderRadius: `${dotSize}px`,
                }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(CarouselImagesWeb);
